"use client";

import * as React from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { isExpectedDomRemovalError } from "@/lib/errors";

export interface ToastProps {
  id: string;
  title?: string;
  description?: string;
  type?: "success" | "error" | "warning" | "info";
  duration?: number;
  onClose?: () => void;
  group?: string;
}

export const TOAST_EXIT_MS = 200;

const getToastStyles = (type: ToastProps["type"]) => {
  switch (type) {
    case "success":
      return {
        className: "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950",
        iconColor: "text-green-600 dark:text-green-400",
        icon: CheckCircle
      };
    case "error":
      return {
        className: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950",
        iconColor: "text-red-600 dark:text-red-400",
        icon: AlertCircle
      };
    case "warning":
      return {
        className: "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950",
        iconColor: "text-yellow-600 dark:text-yellow-400",
        icon: AlertTriangle
      };
    case "info":
    default:
      return {
        className: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950",
        iconColor: "text-blue-600 dark:text-blue-400",
        icon: Info
      };
  }
};

export function Toast({ title, description, type = "info", duration = 5000, onClose }: ToastProps) {
  const { className, iconColor, icon: Icon } = getToastStyles(type);
  const hasClosedRef = React.useRef(false);
  const hasNotifiedCloseRef = React.useRef(false);
  const closeSourceRef = React.useRef<"auto" | "manual">("auto");
  const toastRef = React.useRef<HTMLDivElement | null>(null);
  const [isExiting, setIsExiting] = React.useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updatePreference);
      return () => {
        mediaQuery.removeEventListener("change", updatePreference);
      };
    }

    mediaQuery.addListener(updatePreference);
    return () => {
      mediaQuery.removeListener(updatePreference);
    };
  }, []);

  const notifyClose = React.useCallback((source: "auto" | "manual") => {
    if (hasNotifiedCloseRef.current) {
      return;
    }
    hasNotifiedCloseRef.current = true;
    try {
      onClose?.();
    } catch (error) {
      if (isExpectedDomRemovalError(error)) {
        return;
      }
      if (source === "auto") {
        console.warn("toast onClose error", error);
      } else {
        console.debug("toast manual close error", error);
      }
    }
  }, [onClose]);

  const closeOnce = React.useCallback((source: "auto" | "manual") => {
    if (hasClosedRef.current) {
      return;
    }
    hasClosedRef.current = true;
    closeSourceRef.current = source;
    setIsExiting(true);
    if (prefersReducedMotion) {
      notifyClose(source);
    }
  }, [prefersReducedMotion, notifyClose]);

  React.useEffect(() => {
    if (duration > 0) {
      let isMounted = true;
      const timer = window.setTimeout(() => {
        if (!isMounted) {
          return;
        }
        closeOnce("auto");
      }, duration);
      return () => {
        isMounted = false;
        clearTimeout(timer);
      };
    }
  }, [duration, closeOnce]);

  const handleManualClose = React.useCallback(() => {
    closeOnce("manual");
  }, [closeOnce]);

  const handleAnimationEnd = React.useCallback((event: React.AnimationEvent<HTMLDivElement>) => {
    if (!isExiting) {
      return;
    }
    if (!toastRef.current || event.target !== toastRef.current) {
      return;
    }
    notifyClose(closeSourceRef.current);
  }, [isExiting, notifyClose]);

  return (
    <div
      ref={toastRef}
      onAnimationEnd={handleAnimationEnd}
      className={cn(
        "relative flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg backdrop-blur-sm",
        isExiting ? "animate-toast-exit" : "animate-toast-enter",
        className
      )}
    >
      <Icon className={cn("h-5 w-5 flex-shrink-0 mt-0.5", iconColor)} />
      
      <div className="flex-1 space-y-1">
        {title && (
          <div className="text-sm font-semibold leading-none tracking-tight">
            {title}
          </div>
        )}
        {description && (
          <div className="text-sm opacity-90">
            {description}
          </div>
        )}
      </div>

      <button
        onClick={handleManualClose}
        className="flex-shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// O ToastContainer e o hook useToast foram movidos para o ToastProvider
// em `src/components/ui/toast-context.tsx` para garantir um único
// provedor de estado de toast em toda a aplicação.
